import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { rpaRepository as repo } from '../../../lib/repositories/rpa.repository';
import type {
  DanhSachLicense,
  KetQuaDongBoLicense,
  KetQuaResetThietBi,
  LicenseRpa,
  QuyenOpenCoreLicense,
  ThanCapLicense,
  ThanSuaLicense,
} from '../types';
import { KHOA_RPA, thuLai } from './useRpa';

export function useLicenses(bat = true) {
  return useQuery<DanhSachLicense, ApiError>({
    queryKey: KHOA_RPA.licenses,
    queryFn: () => repo.licenses(),
    enabled: bat,
    staleTime: 15_000,
    retry: thuLai,
  });
}

export function useQuyenLicense(id: string) {
  return useQuery<QuyenOpenCoreLicense, ApiError>({
    queryKey: KHOA_RPA.quyenLicense(id),
    queryFn: () => repo.quyenLicense(id),
    enabled: id !== '',
    staleTime: 15_000,
    retry: thuLai,
  });
}

/**
 * Ghi License đổi cả role OpenCore của User (đồng bộ ngay khi lưu), nên làm mới cả danh bạ OpenCore — kể cả khi hỏng:
 * `502` của OpenCore có thể đến sau khi máy chủ đã ghi database.
 */
function useLamMoiLicense() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KHOA_RPA.licenses });
    void qc.invalidateQueries({ queryKey: KHOA_RPA.danhBaOpenCore });
  };
}

export function useCapLicense() {
  const lamMoi = useLamMoiLicense();
  return useMutation<LicenseRpa, ApiError, ThanCapLicense>({ mutationFn: (than) => repo.capLicense(than), onSettled: lamMoi });
}

export function useSuaLicense() {
  const lamMoi = useLamMoiLicense();
  return useMutation<LicenseRpa, ApiError, { id: string; than: ThanSuaLicense }>({
    mutationFn: ({ id, than }) => repo.suaLicense(id, than),
    onSettled: lamMoi,
  });
}

export function useDoiTrangThaiLicense() {
  const lamMoi = useLamMoiLicense();
  return useMutation<LicenseRpa, ApiError, { id: string; status: 'active' | 'locked' | 'revoked' }>({
    mutationFn: ({ id, status }) => repo.doiTrangThaiLicense(id, status),
    onSettled: lamMoi,
  });
}

export function useResetThietBi() {
  const lamMoi = useLamMoiLicense();
  return useMutation<KetQuaResetThietBi, ApiError, string>({ mutationFn: (id) => repo.resetThietBi(id), onSettled: lamMoi });
}

export function useDongBoLicense() {
  const lamMoi = useLamMoiLicense();
  return useMutation<KetQuaDongBoLicense, ApiError, string>({ mutationFn: (id) => repo.dongBoLicense(id), onSettled: lamMoi });
}

export function useXoaLicense() {
  const lamMoi = useLamMoiLicense();
  return useMutation<void, ApiError, string>({ mutationFn: (id) => repo.xoaLicense(id), onSettled: lamMoi });
}
